import React, { Component, type ReactNode } from 'react';
import { isChunkLoadError } from '@/helpers/appVersion';
import { StaleVersionFallback } from '../StaleVersionFallback/StaleVersionFallback';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (this.state.hasError) {
      if (isChunkLoadError(this.state.error)) {
        return <StaleVersionFallback />;
      }

      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorMessage message={this.state.error?.message || 'An unexpected error occurred'} retry={this.handleReset} />
      );
    }

    return this.props.children;
  }
}
